import { useCallback, useMemo, useState, useRef } from 'preact/hooks';
import useAppDispatch from '../../hooks/useAppDispatch';
import './AssistantHome.less';
import AssistantHomeItemCard from './AssistantHomeItemCard';
import useAppSelector from '../../hooks/useAppSelector';
import HorizontalScrollable from '../scrollable/HorizontalScrollable';
import AssistantFooter from './AssistantFooter';
import Lantern from '../effects/Lantern';
import AssistantWelcomeMessage from './AssistantWelcomeMessage';
import SearchByTextHomeItemFeature from './home-item-feature/SearchByTextHomeItemFeature';
import Button from '../button/Button';
import type { AssistantHomeItem } from '../../models/AssistantHomeItem';
import SearchByImageHomeItemFeature from './home-item-feature/SearchByImageHomeItemFeature';
import type { JSXInternal } from 'preact/src/jsx';
import { useTranslation } from '../../hooks/useTranslation';
import { dispatchEvent } from '../../services/EventService';

const AssistantHome = () => {
    const { t } = useTranslation();
    const [isWelcomeMessageFinished, setWelcomeMessageFinished] =
        useState(false);
    const { mode, position } = useAppSelector(state => state.uiState);
    const { homeItems } = useAppSelector(state => state.homeState);
    const menuFeatureItem = useAppSelector(
        state => state.featureState.menuFeatureItem
    );
    const pageProductId = useAppSelector(state =>
        state.contextState.pageInfo.type === 'product'
            ? state.contextState.pageInfo.productId
            : null
    );
    const pageType = useAppSelector(state => state.contextState.pageInfo.type);
    const dispatch = useAppDispatch();
    const visibleHomeItems = useMemo(() => {
        return homeItems.filter(homeItem => {
            if (homeItem.visibleInPageTypes) {
                return homeItem.visibleInPageTypes.includes(pageType);
            }
            if (homeItem.hiddenInPageTypes) {
                return !homeItem.hiddenInPageTypes.includes(pageType);
            }
            return true;
        });
    }, [homeItems, dispatch, pageType]);
    const ref = useRef<HTMLDivElement>(null);
    const homeItemBackground =
        ref.current !== null &&
        getComputedStyle(ref.current).getPropertyValue('--bri-home-background');

    const expandedHomeItem = visibleHomeItems.find(
        homeItem => homeItem.featureType === menuFeatureItem?.featureType
    );
    const handleHomeItemClick = useCallback(
        async (assistantHomeItem: AssistantHomeItem) => {
            dispatchEvent({
                type: 'bri.click_widget_item',
                isInteraction: true,
                itemId: assistantHomeItem.id
            });
            switch (assistantHomeItem.featureType) {
                case 'product-detail':
                    if (!pageProductId) {
                        return;
                    }
                    await dispatch.featureState.startFeature({
                        featureType: 'product-detail',
                        productId: pageProductId,
                        source: 'brilliant'
                    });
                    break;
                case 'product-search-similar':
                    if (!pageProductId) {
                        return;
                    }
                    await dispatch.featureState.startFeature({
                        featureType: 'product-search-similar',
                        similarToProductIds: [pageProductId]
                    });
                    break;
                case 'product-search-combinations':
                    if (!pageProductId) {
                        return;
                    }
                    await dispatch.featureState.startFeature({
                        featureType: 'product-search-combinations',
                        combinationWithProductIds: [pageProductId]
                    });
                    break;
                case 'product-search-new':
                    await dispatch.featureState.startFeature({
                        featureType: 'product-search-new',
                        productFilter: assistantHomeItem.productFilter
                    });
                    break;
                case 'product-mix-and-match':
                    if (!pageProductId) {
                        return;
                    }
                    await dispatch.featureState.startFeature({
                        featureType: 'product-mix-and-match',
                        productId: pageProductId
                    });
                    break;
                case 'product-search-by-text-menu':
                    await dispatch.featureState.startFeature({
                        featureType: 'product-search-by-text-menu'
                    });
                    break;
                case 'product-search-by-image-menu':
                    await dispatch.featureState.startFeature({
                        featureType: 'product-search-by-image-menu',
                        searchTypes: assistantHomeItem.searchTypes
                    });
                    break;
            }
        },
        [expandedHomeItem, dispatch]
    );
    const heading1 =
        expandedHomeItem?.featureType === 'product-search-by-text-menu'
            ? t('HOME_LINE_1_PRODUCT_SEARCH_BY_TEXT')
            : expandedHomeItem?.featureType === 'product-search-by-image-menu'
              ? t('HOME_LINE_1_PRODUCT_SEARCH_BY_IMAGE')
              : t('HOME_LINE_1');
    const heading2 =
        expandedHomeItem?.featureType === 'product-search-by-text-menu'
            ? t('HOME_LINE_2_PRODUCT_SEARCH_BY_TEXT')
            : expandedHomeItem?.featureType === 'product-search-by-image-menu'
              ? t('HOME_LINE_2_PRODUCT_SEARCH_BY_IMAGE')
              : t('HOME_LINE_2');

    let homeItemFeature: JSXInternal.Element | null = null;
    switch (expandedHomeItem?.featureType) {
        case 'product-search-by-text-menu':
            homeItemFeature = <SearchByTextHomeItemFeature />;
            break;
        case 'product-search-by-image-menu':
            homeItemFeature = <SearchByImageHomeItemFeature />;
            break;
    }
    if (mode !== 'menu') {
        return null;
    }
    return (
        <div
            ref={ref}
            className={`bri-AssistantHome bri-AssistantHome-position-${position}`}
            style={{
                '--bri-assistant-home-cards': Math.min(
                    4,
                    visibleHomeItems.length
                )
            }}
        >
            {!homeItemBackground && <Lantern />}
            <AssistantWelcomeMessage
                mainTitle={t('WELCOME_LINE_1')}
                secondaryTitle={t('WELCOME_LINE_2')}
                onFinished={() => setWelcomeMessageFinished(true)}
            />
            {isWelcomeMessageFinished && (
                <div className={'bri-AssistantHome-content'}>
                    <div>
                        <div
                            className={`bri-AssistantHome-heading1 bri-TitleFont`}
                        >
                            {heading1}
                        </div>
                        <div
                            className={`bri-AssistantHome-heading2 bri-TitleBoldFont`}
                        >
                            {heading2}
                        </div>
                    </div>
                    <HorizontalScrollable
                        hiddenButtons={!!expandedHomeItem}
                        stepDistance={300}
                    >
                        <div
                            className={`bri-AssistantHome-items ${
                                expandedHomeItem ? 'bri-expanded' : ''
                            }`}
                        >
                            {visibleHomeItems.map((homeItem, index) => (
                                <AssistantHomeItemCard
                                    homeItem={homeItem}
                                    onClick={() => {
                                        if (
                                            !expandedHomeItem ||
                                            expandedHomeItem.id !== homeItem.id
                                        ) {
                                            void handleHomeItemClick(homeItem);
                                        }
                                    }}
                                    animationDelay={(2 + index) * 100}
                                    className={
                                        expandedHomeItem
                                            ? expandedHomeItem.id ===
                                              homeItem.id
                                                ? 'bri-expanded'
                                                : 'bri-hidden'
                                            : ''
                                    }
                                >
                                    {homeItemFeature}
                                </AssistantHomeItemCard>
                            ))}
                        </div>
                    </HorizontalScrollable>

                    <AssistantFooter />
                    {expandedHomeItem && (
                        <Button
                            className="bri-AssistantHome-back-button"
                            startIconId="arrow-left"
                            startIconSize="x-small"
                            size="small"
                            variant="text"
                            onClick={() =>
                                dispatch.featureState.setMenuFeatureItem(null)
                            }
                        >
                            {t('GO_BACK')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssistantHome;
